import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Modal, ConfigProvider, Spin, Dropdown, Menu, Pagination} from "antd";
import "./bids-modal.css";
import {PageTitleHeader, StyledButton} from "../../components";
import {useRequest} from "../../utils/hooks";
import moment from "moment";
import {BidStatusLabel} from "./BidStatusLabel";
import {StringBuilder} from "../../AppUtils";
import {NOOP} from "../../utils/NOOP.jsx";
import {BidStatus, BidStatusToColorCompany} from "../../company/CompanyAccountProfile/CompanyBriefs/consts.jsx";
import {TableDateRangeTableAction} from "../../components/OrdersDateRangeTable/TableDateRangeTable";
import {sendReminderToCompany, sendReminderToProvider} from "../../api/briefs/briefs";

export const BriefBidsReceivedModal = ({
    visible,
    onClose,
    bids = [],
    onBidClick = NOOP,
    btnBitTex = "לצפייה",
    bidStatusToColorCompanyMap = BidStatusToColorCompany,
    actions,
    brief,
    reminderBtnTextProvider = "שליחת תזכורת לספק",
    reminderBtnTextCompany = "שליחת תזכורת ללקוח",
    showReminderBtn = true,
    showProviderPhone = false
}) => {
    const [loadingBidId, setLoadingBidId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const PAGE_SIZE = 5;

    const [providersData = {}, isLoadingProviders, error, fetchProviders] = useRequest(
        `/api/briefs/${brief?.briefId}/providers`,
        "POST",
        {
            page: currentPage,
            pageSize: PAGE_SIZE
        },
        [brief?.briefId, currentPage, visible],
        !!(brief?.briefId && currentPage && visible),
        null,
        {
            providers: [],
            totalCount: 0,
            totalPages: 0
        }
    );

    useEffect(() => {
        if (visible) {
            setCurrentPage(1);
        }
    }, [visible]);

    const bidsWithLogos = useMemo(() => {
        if (!providersData.providers || !Array.isArray(providersData.providers)) {
            return [];
        }

        return providersData.providers.flatMap(provider => {
            const bids = Array.isArray(provider.bid) ? provider.bid : provider.bid ? [provider.bid] : [];

            if (bids.length === 0) {
                return [
                    {
                        providerId: provider.providerId,
                        id: provider.providerId,
                        bidId: null,
                        logo:
                            provider.imageUrl ??
                            "https://res.cloudinary.com/well-b-prod/image/upload/c_fill,f_auto,g_faces,q_80,w_500/quLmpxH8h.png",
                        name: provider.brandName ?? "",
                        date: moment(brief?.dtstart).format("DD/MM/YYYY"),
                        time: moment(brief?.dtstart).format("HH:mm"),
                        updatedAt: brief?.updatedAt ?? brief?.createdAt,
                        createdAt: null,
                        providerPhone: provider.contactPhoneNumber
                    }
                ];
            }

            return bids.map(bid => {
                const dtstart = bid?.dtstart ?? brief?.dtstart;
                const updatedAt = bid?.updatedAt ?? bid?.createdAt ?? brief?.updatedAt ?? brief?.createdAt;

                return {
                    ...(bid || {}),
                    providerId: provider.providerId,
                    id: bid?.id ?? bid?.bidId ?? `${provider.providerId}-${bid?.bidId}`,
                    bidId: bid?.bidId,
                    logo:
                        provider.imageUrl ??
                        "https://res.cloudinary.com/well-b-prod/image/upload/c_fill,f_auto,g_faces,q_80,w_500/quLmpxH8h.png",
                    name: provider.brandName ?? "",
                    date: moment(dtstart).format("DD/MM/YYYY"),
                    time: moment(dtstart).format("HH:mm"),
                    updatedAt: updatedAt,
                    createdAt: bid?.createdAt,
                    providerPhone: provider.contactPhoneNumber
                };
            });
        });
    }, [providersData.providers, brief]);

    const handleBidClick = useCallback(
        async bid => {
            if (!bid.bidId) {
                return;
            }
            setLoadingBidId(bid.bidId);
            await onBidClick(bid);
            setLoadingBidId(null);
        },
        [onBidClick]
    );

    const onSendReminderToCompany = useCallback(
        async bid => {
            setLoadingBidId(bid.id);
            await sendReminderToCompany(brief?.briefId);
            setLoadingBidId(null);
        },
        [brief?.briefId]
    );

    const onSendReminderToProvider = useCallback(
        async bid => {
            setLoadingBidId(bid.id);
            await sendReminderToProvider(brief?.briefId, bid.providerId);
            setLoadingBidId(null);
        },
        [brief?.briefId]
    );

    return (
        <Modal
            centered
            width={800}
            className="wb-modal-radius bids-received-modal"
            style={{borderRadius: "8px"}}
            destroyOnClose
            open={visible}
            onCancel={onClose}
            footer={null}
            title={null}
            closable>
            <ConfigProvider direction="rtl">
                <div className="bids-modal-content">
                    <PageTitleHeader
                        className="bids-modal-title"
                        subTitleStyle={{color: "var(--primary-color)"}}
                        showBack={false}>
                        {new StringBuilder(brief?.bids.length || "0").append("הצעות מחיר").toString()}
                    </PageTitleHeader>
                    <p className="bids-modal-subtitle">
                        כל ההצעות שהתקבלו לבקשת הרכישה שלך מופיעות כאן. מה שנשאר לעשות זה לעבור על ההצעה ולאשר אחת מהן.
                    </p>
                    <div className="bids-list">
                        <div className="bid-row" style={{fontWeight: "bold"}}>
                            <div className="bid-info">בית העסק</div>
                            <div className="bid-details">שליחת הצעת מחיר</div>
                            <div className="bid-actions" style={{margin: 0}}>
                                סטטוס
                            </div>
                            <div className="bids-table-header-action"></div>
                        </div>
                        {isLoadingProviders ? (
                            <Spin />
                        ) : (
                            bidsWithLogos.map(bid => (
                                <div key={bid.id} className="bid-row">
                                    <div className="bid-info">
                                        {bid.logo ? (
                                            <img src={bid.logo} alt="" className="bid-logo" />
                                        ) : (
                                            <div className="bid-logo-placeholder" />
                                        )}
                                        <div className="bid-name">
                                            {bid.name}
                                            {showProviderPhone && <div className="bid-phone">{bid.providerPhone}</div>}
                                        </div>
                                    </div>
                                    <div className="bid-details">
                                        {bid.createdAt ? (
                                            <>
                                                <div className="bid-date">
                                                    {moment(bid.createdAt).format("DD.MM.YYYY")}
                                                </div>
                                                <div className="bid-time">{moment(bid.createdAt).format("HH:mm")}</div>
                                            </>
                                        ) : (
                                            <span>-</span>
                                        )}
                                    </div>
                                    <div className="bid-actions">
                                        <BidStatusLabel
                                            bid={bid}
                                            bidStatusToColorCompanyMap={bidStatusToColorCompanyMap}
                                            brief={brief}
                                        />
                                        {loadingBidId === bid.id ? (
                                            <Spin />
                                        ) : (
                                            <>
                                                {bid?.bidId &&
                                                    (showReminderBtn
                                                        ? !bid?.watchedByAdmin
                                                        : bid?.status !== BidStatus.accepted &&
                                                          bid?.status !== BidStatus.cancelled) && (
                                                        <StyledButton
                                                            className="square-button primary-color-button"
                                                            style={{
                                                                backgroundColor: "var(--primary-color)",
                                                                color: "#fff"
                                                            }}
                                                            onClick={() => handleBidClick(bid)}>
                                                            {btnBitTex}
                                                        </StyledButton>
                                                    )}
                                                {showReminderBtn &&
                                                    bid?.status !== BidStatus.accepted &&
                                                    bid?.status !== BidStatus.cancelled &&
                                                    bid?.bidId &&
                                                    bid?.watchedByAdmin &&
                                                    bid?.status !== BidStatus.haveNote && (
                                                        <StyledButton
                                                            className="square-button primary-color-button"
                                                            style={{
                                                                backgroundColor: "var(--link-color)",
                                                                color: "#fff"
                                                            }}
                                                            onClick={() => onSendReminderToCompany(bid)}>
                                                            {reminderBtnTextCompany}
                                                        </StyledButton>
                                                    )}
                                                {showReminderBtn &&
                                                    bid?.status !== BidStatus.accepted &&
                                                    bid?.status !== BidStatus.cancelled &&
                                                    ((!bid?.bidId &&
                                                        !brief?.removedProviderIds?.includes(bid.providerId) &&
                                                        brief?.watchedByAdmin) ||
                                                        (bid?.bidId && bid?.status === BidStatus.haveNote)) && (
                                                        <StyledButton
                                                            className="square-button primary-color-button"
                                                            style={{
                                                                backgroundColor: "var(--link-color)",
                                                                color: "#fff"
                                                            }}
                                                            onClick={() => onSendReminderToProvider(bid)}>
                                                            {reminderBtnTextProvider}
                                                        </StyledButton>
                                                    )}
                                                {actions.length > 0 && bid.bidId ? (
                                                    <TableDateRangeTableAction
                                                        actions={actions.map(action => ({
                                                            ...action,
                                                            onClick: action.shouldFetch
                                                                ? async () => {
                                                                      await action.onClick(bid);
                                                                      fetchProviders();
                                                                  }
                                                                : () => action.onClick(bid)
                                                        }))}
                                                        item={bid}
                                                    />
                                                ) : (
                                                    <div style={{width: "25px"}}></div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                        {providersData.totalPages > 1 && (
                            <div
                                style={{
                                    display: "flex",
                                    direction: "rtl",
                                    justifyContent: "center",
                                    marginTop: "20px"
                                }}>
                                <Pagination
                                    current={currentPage}
                                    total={providersData.totalCount}
                                    pageSize={PAGE_SIZE}
                                    onChange={page => setCurrentPage(page)}
                                    showSizeChanger={false}
                                    showTotal={(total, range) => `${range[0]}-${range[1]} מתוך ${total}`}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </ConfigProvider>
        </Modal>
    );
};
